import { FC, useState, useEffect } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { RolesPermissionsListLoading } from '../components/loading/RolesPermissionsListLoading'
import { createRolePermission, updateRolePermission } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage, useIntl } from 'react-intl'
import { RolePermission } from '../core/_models'
import axios from 'axios'

type Props = {
  isRolePermissionLoading: boolean
  rolepermission: RolePermission
}

type Permission = {
  id: string
  name: string
}

const editRolePermissionSchema = Yup.object().shape({
  name: Yup.string().required('Role name is required'),
})

const RolePermissionEditModalForm: FC<Props> = ({ rolepermission, isRolePermissionLoading }) => {
  const intl = useIntl()
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const [permissions, setPermissions] = useState<Permission[]>([])
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(false)

  useEffect(() => {
    // Fetch all permissions
    axios
      .get(`${process.env.REACT_APP_THEME_API_URL}/permissions`)
      .then((response) => {
        if (response.data.success) {
          setPermissions(response.data.data.permissions)
          // Populate selected permissions for the role being edited
          setSelectedPermissions(rolepermission.permissions || [])
        }
      })
      .catch((error) => console.error('Error fetching permissions:', error))
  }, [rolepermission]) // Reload when rolepermission changes

  const handlePermissionChange = (id: string) => {
    setSelectedPermissions((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((permissionId) => permissionId !== id)
        : [...prevSelected, id]
    )
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPermissions([]) // Deselect all
    } else {
      setSelectedPermissions(permissions.map((permission) => permission.id)) // Select all
    }
    setSelectAll(!selectAll)
  }

  const cancel = () => {
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: {
      name: rolepermission.name || '',
    },
    validationSchema: editRolePermissionSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const payload = {
          ...values,
          id: rolepermission.id, // Include role ID for update
          permissions: selectedPermissions, // Send selected permissions
        };

        console.log('Payload being sent:', payload);

        if (rolepermission.id) {
          // Update role
          await updateRolePermission(payload);
        } else {
          // Create new role
          await createRolePermission(payload);
        }

        // Trigger refetch after successful submission
        refetch();
      } catch (ex) {
        console.error('Error during submission:', ex);
      } finally {
        setSubmitting(false);
        cancel();
      }
    },
  });


  return (
    <form
      id='kt_modal_add_rolepermission_form'
      className='form'
      onSubmit={formik.handleSubmit}
      noValidate
    >
      {/* Role Name */}
      <div className='fv-row mb-7'>
        <label className='required fw-bold fs-6 mb-2'>
          <FormattedMessage id='ROLEPERMISSION.FORM.INPUT.NAME' defaultMessage={'Role Name'} />
        </label>
        <input
          placeholder={intl.formatMessage({ id: 'ROLEPERMISSION.FORM.PLACEHOLDER.NAME', defaultMessage: 'Enter role name' })}
          {...formik.getFieldProps('name')}
          className={clsx(
            'form-control form-control-solid mb-3',
            { 'is-invalid': formik.touched.name && formik.errors.name },
            { 'is-valid': formik.touched.name && !formik.errors.name }
          )}
          type='text'
          name='name'
          autoComplete='off'
          disabled={formik.isSubmitting || isRolePermissionLoading}
        />
        {formik.touched.name && formik.errors.name && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{"" + formik.errors.name}</span>
          </div>
        )}
      </div>

      {/* Permissions */}
      <div className='fv-row mb-7'>
        <label className='fw-bold fs-6 mb-2'>
          <FormattedMessage id='ROLEPERMISSION.FORM.INPUT.PERMISSIONS' defaultMessage={'Permissions'} />
        </label>
        <div className='form-check mb-3'>
          <input
            className='form-check-input'
            type='checkbox'
            id='select-all'
            checked={selectAll}
            onChange={handleSelectAll}
          />
          <label className='form-check-label fw-bold' htmlFor='select-all'>
            <FormattedMessage id='ROLEPERMISSION.FORM.INPUT.SELECTALL' defaultMessage={'Select All'} />
          </label>
        </div>
        <div className='row'>
          {permissions.map((permission) => (
            <div key={permission.id} className='col-md-4 col-sm-6 mb-3'>
              <div className='form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id={`permission-${permission.id}`}
                  value={permission.id}
                  checked={selectedPermissions.includes(permission.id)}
                  onChange={() => handlePermissionChange(permission.id)}
                />
                <label className='form-check-label' htmlFor={`permission-${permission.id}`}>
                  {permission.name}
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form Actions */}
      <div className='text-center pt-15'>
        <button
          type='reset'
          onClick={() => cancel()}
          className='btn btn-light me-3'
          disabled={formik.isSubmitting || isRolePermissionLoading}
        >
          <FormattedMessage id='FORM.GENERAL.DISCARD' />
        </button>
        <button
          type='submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || isRolePermissionLoading}
        >
          <span className='indicator-label'>
            <FormattedMessage id='FORM.GENERAL.SUBMIT' />
          </span>
          {(formik.isSubmitting || isRolePermissionLoading) && (
            <span className='indicator-progress'>
              <FormattedMessage id='FORM.GENERAL.PLEASEWAIT' />
              {' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}

export { RolePermissionEditModalForm }
